import {
  partsTableUrl,
  pnUrl,
  ffUrl,
  assetURL,
} from "../../../Experience/World/config";
import * as THREE from "three";

import debug from "debug";

const log = debug("app:globalTable");

export class GlobalTableData {
  constructor(container, currentPageInput, eventHub, experience) {
    this.experience = experience;
    this.container = container;
    this.currentPageInput = currentPageInput;
    this.eventHub = eventHub;
    this.isMouseOverRow = false;
    this.currentPage = 1;
    this.limit = 10;
    this.searchBy = "";
    this.hasMorePages = false;

    this.init();
    this.currentPageData = [];
    this.latestData = [];

    this.lastPointerWorld = null;   // {x,y,z} from Functions
    this.placeAtPointerNext = false; // armed by Enter shortcut

    this.fetchFromDataVault = (requestEvent, id, responseEvent) => {
      return new Promise((resolve, reject) => {
        const responseHandler = (data) => {
          log(`Received data for ${responseEvent}:`, data);
          resolve({
            ok: true,
            headers: new Headers(),
            json: () => Promise.resolve({ data }),
          });
          this.eventHub.off(responseEvent, responseHandler);
        };

        this.eventHub.on(responseEvent, responseHandler);
        log(`Emitting ${requestEvent} with id:`, id);
        this.eventHub.emit(requestEvent, id);
      });
    };
  }

  cleanup() {
    log("GlobalTableData cleanup started...");
  
    // 1. Remove event listeners from eventHub
    this.eventHub.off("globalSearch", this.handleGlobalSearch);
  
    // 2. Remove event listeners from DOM elements
    if (this.currentPageInput) {
      this.currentPageInput.removeEventListener("change", this.handlePageChange);
    }
  
    if (this.container) {
      const dynamicSearchTable = this.container.querySelector("#dynamicSearchTable");
      if (dynamicSearchTable) {
        dynamicSearchTable.querySelectorAll("tr").forEach((row) => {
          row.removeEventListener("mouseover", this.handleRowMouseOver);
          row.removeEventListener("mouseout", this.handleRowMouseOut);
          row.removeEventListener("click", this.handleRowClick);
        });
      }
    }
  
    // 3. Clear object references
    this.container = null;
    this.currentPageInput = null;
    this.eventHub = null;
    this.isMouseOverRow = false;
    this.searchBy = "";
    this.ffIdPnd = null;
    this.layerPn = null;
    this.pricePn = null;
    this.imageBrd = null;
    this.properties = null;
    this.partNumberVl = null;
    this.currentPage = 1;
  
    log("GlobalTableData cleanup completed.");
  }
  /**
   * Initializes event listeners and triggers the first data fetch.
   */
  init() {
    this.eventHub.on("globalSearch", async (eventData) => {
      if (!eventData || !eventData.searchBy) {
        log("Invalid event data");
        return;
      }

      log("Custom event triggered! Search input changed.");
      this.searchBy = eventData.searchBy;

      if (this.searchBy.trim() !== "") {
        this.container.style.display = "flex";

        document.querySelector(".drag-table").style.display = "none";
        document.querySelector(".drag-table-container").style.display = "none";

        try {
          const data = await this.fetchDataFromServer(this.searchBy);
          this.updateTable(data);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    });

    // live, snapped world coords from Functions.js
    this.eventHub.on("globalPointerWorld", (pt) => {
      if (pt && typeof pt.x === "number" && typeof pt.y === "number" && typeof pt.z === "number") {
        this.lastPointerWorld = pt;
      }
    });

    // Enter pressed in keyboard layer -> place first row at pointer
    this.eventHub.on("globalSearchConfirm", () => {
      try {
        this.placeAtPointerNext = true;     // one-shot arm for this action
        this.placeFirstResultIfAvailable();
      } catch (e) {
        console.error("globalSearchConfirm failed:", e);
        this.placeAtPointerNext = false;
      }
    });

    this.eventHub.on("globalSearchOpen", () => {
      // Show container
      if (this.container) this.container.style.display = "flex";

      // Hide drag table if present (matches your current behavior)
      const drag = document.querySelector(".drag-table");
      const dragContainer = document.querySelector(".drag-table-container");
      if (drag) drag.style.display = "none";
      if (dragContainer) dragContainer.style.display = "none";

      // Focus search input if it exists
      const input = document.getElementById("searchResultsText");
      if (input) {
        input.focus();

        // If there’s text already, reuse your existing fetch path
        const value = String(input.value || "").trim();
        if (value) {
          this.eventHub.emit("globalSearch", { searchBy: value });
        }
      }
    });

    this.eventHub.on("globalSearchClose", () => {
      // reset one-shot placement arm
      this.placeAtPointerNext = false;

      // hide the table + blur input + refocus canvas
      this.hideGlobalTableContainer();

      // also hide hover preview if it’s up
      const preview = document.getElementById("image-preview-container");
      if (preview) {
        preview.innerHTML = "";
        preview.style.display = "none";
      }
    });


    this.fetchDataAndUpdateTable();
  }

  async fetchDataAndUpdateTable(searchBy = this.searchBy) {
    try {
      const data = await this.fetchDataFromServer(
        searchBy,
        this.currentPage,
        this.limit
      );

      if (data && data.values) {
        this.currentPageInput.value = this.currentPage;
        // Use hasMorePages from data if available, otherwise infer from data length
        this.hasMorePages = data.hasMorePages !== undefined 
          ? data.hasMorePages 
          : (data.values.length === this.limit && data.values.length > 0);
        this.updateTable(data);
        // Emit event to update pagination button states
        this.eventHub.emit("globalTablePaginationUpdate", {
          hasMorePages: this.hasMorePages,
          currentPage: this.currentPage,
          hasData: data.values.length > 0
        });
      } else {
        console.error("No data available for the current page");
        this.hasMorePages = false;
        // If we're on a page with no data, go back to the last valid page
        if (this.currentPage > 1) {
          this.currentPage -= 1;
          this.currentPageInput.value = this.currentPage;
          // Recursively fetch the previous page
          return this.fetchDataAndUpdateTable(searchBy);
        }
        this.eventHub.emit("globalTablePaginationUpdate", {
          hasMorePages: false,
          currentPage: this.currentPage,
          hasData: false
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      this.hasMorePages = false;
      this.eventHub.emit("globalTablePaginationUpdate", {
        hasMorePages: false,
        currentPage: this.currentPage,
        hasData: false
      });
    }
  }

  async fetchDataFromServer(searchBy, page = 1, limit = 10) {
    const dv = this.experience?.dataVault;

    // ✅ Try fullSearchTable
    if (dv?.fullSearchTable) {
      return dv.getSearchResults({ searchBy, page, limit });
    }

    // ✅ Check individual result cache
    if (dv?.hasCachedSearchResults?.(searchBy, page)) {
      return dv.getCachedSearchResults(searchBy, page);
    }

    // ❌ Fallback to network
    try {
      const response = await this.fetchWithAuthHeaders(partsTableUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ searchBy, page, limit }),
      });
      if (!response.ok) throw new Error("Network fetch failed");
      const result = await response.json();

      dv?.cacheSearchResults?.(searchBy, page, result);
      return result;
    } catch (err) {
      console.error("Error fetching data:", err);
      return { headers: [], values: [] };
    }
  }



  updateTable(data) {
    const existingWrapper = document.getElementById("globalTableScrollWrapper");
    if (existingWrapper) {
      existingWrapper.remove();
    }

    const wrapper = document.createElement("div");
    wrapper.id = "globalTableScrollWrapper";
    wrapper.className = "global-table-scroll-wrapper";
    const newTable = this.createTableFromData(data);
    wrapper.appendChild(newTable);
    this.container.appendChild(wrapper);
  }

  createTableFromData(data) {
    const globalDynamicTable = document.createElement("table");
    globalDynamicTable.className = "dynamic-search-table";
    globalDynamicTable.id = "dynamicSearchTable";

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    data.headers.forEach((header) => {
      const th = document.createElement("th");
      th.textContent = header.name;
      th.style.border = "1px solid #ddd";
      th.style.padding = "8px";
      th.style.textAlign = "left";
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    globalDynamicTable.appendChild(thead);

    const tbody = document.createElement("tbody");
    data.values.forEach((rowData) => {
      const globalTableTr = document.createElement("tr");
      rowData.forEach((cellData) => {
        const td = document.createElement("td");
        td.textContent = cellData;
        td.style.border = "1px solid #ddd";
        td.style.padding = "8px";
        globalTableTr.appendChild(td);
      });

      this.currentPageData = Array.isArray(data?.values) ? [...data.values] : [];
      this.latestData = data?.values || [];

      globalTableTr.addEventListener("mouseover", () => {
        this.handleRowMouseOver(rowData);
      });

      globalTableTr.addEventListener("mouseout", () => {
        this.handleRowMouseOut();
      });

      globalTableTr.addEventListener("click", (e) => {
        this.handleRowClick(rowData);
      });

      tbody.appendChild(globalTableTr);
    });

    globalDynamicTable.appendChild(tbody);

    return globalDynamicTable;
  }

  async handleRowClick(rowData) {
    // keep your scene prep signals
    this.eventHub.emit("alignPlane");
    this.eventHub.emit("explosionStop");

    // If not armed by Enter, preserve current dev behavior
    const usePointer = this.placeAtPointerNext && this.lastPointerWorld;
    if (!usePointer) {
      this.eventHub.emit("placePartTopRight", rowData);
      this.hideGlobalTableContainer();
      return;
    }

    // === Enter-confirm path: emit MakePartsEvent with coords ===
    const pnValue = rowData[0];
    const dv = this.experience?.dataVault;

    try {
      // part (cache first)
      let part = dv?.parts?.get(pnValue);
      if (!part) {
        const urlForPn = `${pnUrl}/${pnValue}`;
        const partResp = await this.fetchWithAuthHeaders(urlForPn);
        if (!partResp.ok) throw new Error(`Part fetch failed: ${partResp.status}`);
        part = await partResp.json();
        dv?.parts?.set(pnValue, part);
      }

      // form factor (cache first)
      let ffData = dv?.formFactors?.get(part.ffId);
      if (!ffData) {
        const urlForFfid = `${ffUrl}/${part.ffId}`;
        const ffResp = await this.fetchWithAuthHeaders(urlForFfid);
        if (!ffResp.ok) throw new Error(`FF fetch failed: ${ffResp.status}`);
        ffData = await ffResp.json();
        dv?.formFactors?.set(part.ffId, ffData);
      }

      // normalize props as in main
      const props = {};
      (part.props || []).forEach((p) => { props[p.label] = p.value; });
      props["FormFactor"] = part.ffId;

      const figure = {
        partData: {
          fullpartnumber: part.id ?? pnValue,
          layer: part.layer,
          properties: props,
        },
        x: this.lastPointerWorld.x,
        y: this.lastPointerWorld.y, // Functions emits -1.5 here
        z: this.lastPointerWorld.z,
        angle: 0,
      };

      this.eventHub.emit("MakePartsEvent", {
        eventType: "add",
        data: { figures: [figure] },
      });

    } catch (err) {
      console.error("[GlobalTableData] Pointer placement failed:", err);
      // graceful fallback: your prior behavior
      this.eventHub.emit("placePartTopRight", rowData);
    } finally {
      this.placeAtPointerNext = false; // one-shot disarm
      this.hideGlobalTableContainer();
    }
  }


  

  async handleRowMouseOver(rowData) {
    this.isMouseOverRow = true;
    const pnValue = rowData[0];
    const dv = window.experience?.dataVault; // or pass it into the class if preferred

    try {
      // ✅ Try cache first
      let part = dv?.parts?.get(pnValue);
      if (!part) {
        const urlForPn = `${pnUrl}/${pnValue}`;
        const response = await this.fetchWithAuthHeaders(urlForPn);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        part = await response.json();
        dv?.parts?.set(pnValue, part);
      }

      this.ffIdPnd = part.ffId;
      this.layerPn = part.layer;
      this.properties = part.props;

      // ✅ Try formFactor cache
      let ffData = dv?.formFactors?.get(this.ffIdPnd);
      if (!ffData) {
        const urlForFfid = `${ffUrl}/${this.ffIdPnd}`;
        const ffResponse = await this.fetchWithAuthHeaders(urlForFfid);
        if (!ffResponse.ok) throw new Error(`HTTP error! Status: ${ffResponse.status}`);
        ffData = await ffResponse.json();
        dv?.formFactors?.set(this.ffIdPnd, ffData);
      }

      // ✅ Show preview image
      if (this.isMouseOverRow) {
        const imagePreviewContainer = document.getElementById("image-preview-container");
        const cachedImageURL = await this.getCachedImage(ffData.images[0]?.imageURL || "");

        const globalImageElement = document.createElement("img");
        globalImageElement.src = cachedImageURL;
        globalImageElement.classList.add("hover-image-preview");
        globalImageElement.style.width = ffData.w * 48 + "px";
        globalImageElement.style.height = ffData.h * 48 + "px";

        imagePreviewContainer.innerHTML = "";
        imagePreviewContainer.appendChild(globalImageElement);
        imagePreviewContainer.style.display = "block";
      }
    } catch (error) {
      console.error("Error in handleRowMouseOver:", error);
    }
  }

  async handleRowMouseOut() {
    this.isMouseOverRow = false;
    const imagePreviewContainer = document.getElementById("image-preview-container");
    imagePreviewContainer.innerHTML = "";
    imagePreviewContainer.style.display = "none";
  }

  async getCachedImage(imagePath) {
    log("Fetching image:", imagePath); // Debugging

    return new Promise((resolve) => {
        const dbRequest = indexedDB.open("ImageCacheDB");

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            const transaction = db.transaction("images", "readonly");
            const store = transaction.objectStore("images");

            // Ensure "cascade" is in the URL
            const formattedPath = `https://layoutxmw-assets.xmicrowave.com/cascade${imagePath}`;
            //console.log("Checking IndexedDB for:", formattedPath);

            const request = store.get(formattedPath);

            request.onsuccess = async () => {
                if (request.result && request.result.blob) {
                    //console.log("Image found in cache:", formattedPath);
                    resolve(URL.createObjectURL(request.result.blob));
                } else {
                    console.warn("Image not found in cache, fetching from API:", formattedPath);
                    resolve(await this.fetchAndCacheImage(imagePath));
                }
            };

            request.onerror = () => {
                console.error("IndexedDB request failed for:", formattedPath);
                resolve(this.fetchAndCacheImage(imagePath));
            };
        };
    });
}


async fetchAndCacheImage(imagePath) {
  // Ensure "cascade" is in the URL
  const imageUrl = `https://layoutxmw-assets.xmicrowave.com/cascade${imagePath}`;

  try {
      const response = await fetch(imageUrl);


      if (!response.ok) {
          throw new Error(`Image fetch failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      //console.log("Fetched image from API:", imageUrl);

      const dbRequest = indexedDB.open("ImageCacheDB");

      dbRequest.onsuccess = () => {
          const db = dbRequest.result;
          const transaction = db.transaction("images", "readwrite");
          const store = transaction.objectStore("images");

          // Store the URL inside the object
          store.put({ url: imageUrl, blob: blob });

          //console.log("Image cached in IndexedDB:", imageUrl);
      };

      return URL.createObjectURL(blob);
  } catch (error) {
      console.error("Failed to fetch and cache image:", imageUrl, error);
      return ""; // Return empty string or placeholder image
  }
}
async fetchWithAuthHeaders(url, options = {}) {
  return new Promise((resolve) => {
    this.eventHub.emit("tokenRequest");
    this.eventHub.once("tokenResponse", async (token) => {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(options.headers || {})
      };

      try {
        const response = await fetch(url, {
          ...options,
          headers
        });

        resolve(response);
      } catch (err) {
        console.error(`[GlobalTableData] Auth fetch failed: ${err.message}`);
        resolve({ ok: false, status: 500, json: async () => ({ error: err.message }) });
      }
    });
  });
}

hideGlobalTableContainer() {
  if (this.container) {
    this.container.style.display = "none";
  }

  // 🧹 Clear & blur the search bar so keyboard shortcuts aren't blocked
  const input = document.getElementById("searchResultsText");
  if (input) {
    input.value = "";          // optional: clear remembered text
    input.blur();              // important: exit typing mode
  }

  // 🎯 Return focus to canvas so Space works next time
  const canvas =
    this.experience?.canvas ||
    this.experience?.renderer?.domElement ||
    document.getElementById("modelCanvas") ||
    document.querySelector("canvas.webgl");

  if (canvas && typeof canvas.focus === "function") {
    canvas.focus({ preventScroll: true });
  }
}


placeFirstResultIfAvailable() {
  if (!this.container || this.container.style.display !== "flex") return;

  let first = this.currentPageData && this.currentPageData[0];

  if (!first) {
    const firstRow = document.querySelector("#dynamicSearchTable tbody tr");
    if (firstRow) first = Array.from(firstRow.cells).map((td) => td.textContent);
  }

  if (!first) return;
  this.handleRowClick(first);
}


}